import _, { Dictionary, groupBy } from "lodash"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { SupplierResponses } from "@tfg/types/endpoints/supplier"
import {
  CustomFieldDocumentRequirement,
  OperationChainActivities,
  OperationNodeId,
  TraceabilityResponses,
} from "@tfg/types/endpoints/traceability"

import { FindOperationChainsResponseDto } from "~/gen/api"
import { isProdsiteFieldRequestSubmitted } from "~/pages/SupplierPortal/TraceabilityRequests/components/ProdsiteRequests"
import { getCustomFieldParser } from "~/pages/SupplierPortal/TraceabilityRequests/components/getCustomFieldParser.ts"
import { OperationState } from "~/pages/SupplierPortal/TraceabilityRequests/types.ts"
import { GetTranslationCallback } from "~/utils/translations"

export type TraceabilityOperation =
  TraceabilityResponses.TraceabilityRequestOperation & {
    isTransferred: boolean
  }

export type OperationItem = {
  activityName: string
  activityId: string
  item: TraceabilityResponses.TraceabilityRequestItem
  nodeId: OperationNodeId
  rank: number
  isFirstActivity: boolean
  parentNodeId?: OperationNodeId
  operation: TraceabilityOperation | null
  operationCompletionStatus: OperationCompletionStatus | null
  customFieldDefs: TraceabilityResponses.PublicCustomFieldDef[]
  extraCustomFieldDefs: TraceabilityResponses.PublicCustomFieldDef[]
  hiddenCustomFieldDefs: TraceabilityResponses.PublicCustomFieldDef[]
  parentOperation?: TraceabilityOperation
  isInInformationRequest: boolean
  isDuplicateProductionSite?: boolean
}

export type RequestWithOpItem = {
  original: TraceabilityResponses.TraceabilityRequest
  opItems: OperationItem[]
  opItemsByRank: Dictionary<OperationItem[]>
  error?: string
}

function itemToOperationChainActivities({
  item,
  operationChains,
}: {
  item: TraceabilityResponses.TraceabilityRequestItem
  operationChains: FindOperationChainsResponseDto[]
}): OperationChainActivities {
  let productOperationChains: FindOperationChainsResponseDto[] = []
  let componentOperationChains: FindOperationChainsResponseDto[] = []
  let materialOperationChains: FindOperationChainsResponseDto[] = []

  switch (item.type) {
    case "product":
      productOperationChains = operationChains.filter(
        (oc) => oc.type === "PRODUCT"
      )

      return (
        productOperationChains
          .find((oc) => oc.name === item.operationChainName)
          ?.activities.map((activity) => ({
            activityId: activity.id,
            activityName: activity.name,
            type: "product",
            position: activity.position,
          })) ?? []
      )
    case "component":
      componentOperationChains = operationChains.filter(
        (oc) => oc.type === "COMPONENT"
      )

      return (
        componentOperationChains
          .find((oc) => oc.name === item.operationChainName)
          ?.activities.map((activity) => ({
            activityId: activity.id,
            activityName: activity.name,
            type: "component",
            position: activity.position,
          })) ?? []
      )
    case "material":
      materialOperationChains = operationChains.filter(
        (oc) => oc.type === "MATERIAL"
      )

      return (
        materialOperationChains
          .find((oc) => oc.name === item.operationChainName)
          ?.activities.map((activity) => ({
            activityId: activity.id,
            activityName: activity.name,
            type: "material",
            position: activity.position,
          })) ?? []
      )
  }
}

type CustomFieldByActivity = _.Dictionary<
  TraceabilityResponses.PublicCustomFieldDef[]
>

function buildOperationTree({
  startingItem,
  customFieldDefByActivity,
  extraCustomFieldDefs,
  hiddenCustomFieldDefs,
  startingActivityName,
  operationChains,
  isReinforcedTraceability,
  informationRequestId,
  informationRequestOwner,
}: {
  startingItem: TraceabilityResponses.TraceabilityRequestItem
  customFieldDefByActivity: CustomFieldByActivity
  extraCustomFieldDefs: CustomFieldByActivity
  hiddenCustomFieldDefs: CustomFieldByActivity
  startingActivityName: string
  operationChains: FindOperationChainsResponseDto[]
  isReinforcedTraceability: boolean
  informationRequestId: number
  informationRequestOwner: string
}): { opItems: OperationItem[]; error?: string } {
  const chainOperations = _.sortBy(
    itemToOperationChainActivities({
      item: startingItem,
      operationChains,
    }),
    "position"
  )

  const indexStartingActivity = chainOperations.findIndex(
    (x) => x.activityName === startingActivityName
  )
  if (indexStartingActivity === -1) {
    const chainNames = chainOperations.map((x) => x.activityName).join(", ")
    console.error(
      `Starting activity ${startingActivityName} not found in operation chain ${chainNames}`,
      {
        startingActivity: startingActivityName,
        chainNames,
      }
    )
    return {
      opItems: [],
      error: `Starting activity ${startingActivityName} not found in operation chain ${chainNames}`,
    }
  }

  const startingOpchain = chainOperations.slice(indexStartingActivity)
  const opItems = buildOperationItems({
    customFieldDefByActivity,
    extraCustomFieldDefs,
    hiddenCustomFieldDefs,
    chainOperations: startingOpchain,
    item: startingItem,
    rank: 1,
    isFirstActivity:
      indexStartingActivity === 0 && startingItem.type === "product",
    parentNodeId: undefined,
    operationChains,
    isReinforcedTraceability,
    informationRequestId,
    informationRequestOwner,
  })
  return { opItems }
}

const buildOperationItems = (params: {
  customFieldDefByActivity: CustomFieldByActivity
  extraCustomFieldDefs: CustomFieldByActivity
  hiddenCustomFieldDefs: CustomFieldByActivity
  chainOperations: OperationChainActivities
  item: TraceabilityResponses.TraceabilityRequestItem
  rank: number
  isFirstActivity: boolean
  parentNodeId?: OperationNodeId
  parentOperation?: TraceabilityOperation
  operationChains: FindOperationChainsResponseDto[]
  isReinforcedTraceability: boolean
  informationRequestId: number
  informationRequestOwner: string
}): OperationItem[] => {
  const {
    customFieldDefByActivity,
    extraCustomFieldDefs,
    hiddenCustomFieldDefs,
    chainOperations,
    item,
    rank,
    isFirstActivity,
    parentNodeId,
    parentOperation,
    operationChains,
    isReinforcedTraceability,
    informationRequestId,
    informationRequestOwner,
  } = params

  if (chainOperations.length === 0) return []

  const head = chainOperations[0]
  const nodeId: OperationNodeId = `${head.type}-${head.activityName}-${item.id}`
  const tail = chainOperations.slice(1)

  const foundOperation = item.operations.find(
    (op) =>
      op.activity.id === head.activityId &&
      (op.informationRequestId === informationRequestId ||
        op.owner.id !== informationRequestOwner)
  )

  let currentNode: OperationItem
  let isTransferred = false
  if (foundOperation) {
    isTransferred = getIsTransferred(
      foundOperation,
      isReinforcedTraceability,
      informationRequestId === foundOperation.informationRequestId
    )

    currentNode = {
      activityName: head.activityName,
      activityId: head.activityId,
      item,
      customFieldDefs: customFieldDefByActivity[head.activityId] || [],
      extraCustomFieldDefs: extraCustomFieldDefs[head.activityId] || [],
      hiddenCustomFieldDefs: hiddenCustomFieldDefs[head.activityId] || [],
      nodeId,
      isFirstActivity,
      parentNodeId,
      parentOperation,
      operation: {
        ...foundOperation,
        isTransferred,
      },
      operationCompletionStatus: null,
      rank,
      isInInformationRequest:
        informationRequestId === foundOperation.informationRequestId,
    }
  } else {
    currentNode = {
      activityName: head.activityName,
      activityId: head.activityId,
      item,
      customFieldDefs: [],
      extraCustomFieldDefs: [],
      hiddenCustomFieldDefs: [],
      nodeId,
      isFirstActivity,
      parentNodeId,
      parentOperation,
      operation: null,
      operationCompletionStatus: { progress: 100, isComplete: true },
      rank,
      isInInformationRequest: false,
    }
  }
  const nextNodes: OperationItem[] =
    tail.length > 0
      ? buildOperationItems({
          ...params,
          chainOperations: tail,
          rank: rank + 1,
          isFirstActivity: false,
          parentNodeId: nodeId,
          parentOperation: foundOperation
            ? {
                ...foundOperation,
                isTransferred,
              }
            : parentOperation,
        })
      : item.children.flatMap((child) =>
          buildOperationItems({
            ...params,
            item: child,
            chainOperations: itemToOperationChainActivities({
              item: child,
              operationChains,
            }),
            rank: rank + 1,
            isFirstActivity: false,
            parentNodeId: nodeId,
            parentOperation: foundOperation
              ? {
                  ...foundOperation,
                  isTransferred,
                }
              : parentOperation,
          })
        )

  return [currentNode, ...nextNodes]
}

export interface RequestOperation {
  activityName: string
  item: TraceabilityResponses.TraceabilityRequestItem
  nodeId: OperationNodeId
  parentNodeId?: OperationNodeId
}

export interface OperationsByType {
  type: "material" | "component" | "product"
  item: TraceabilityResponses.TraceabilityRequestItem
  operations: RequestOperation[]
}

const isCustomFieldComplete = (operationItem: OperationItem) => {
  return operationItem.customFieldDefs.every((cfd) => {
    const value = operationItem.operation?.customFieldsValues?.find(
      (x) => x.customFieldDefinitionId === cfd.id
    )?.value
    if (!value || value === "") return false
    const enumValues = cfd.enumValuesTranslated.map((x) => x.originalText)
    const parser = getCustomFieldParser(cfd, enumValues)
    const parsed = parser.safeParse(value)
    return parsed.success
  })
}

export const isProductItem = (
  item: TraceabilityResponses.TraceabilityRequestItem
): item is TraceabilityResponses.ProductTraceabilityRequestItem =>
  item.type === "product"

export const isComponentItem = (
  item: TraceabilityResponses.TraceabilityRequestItem
): item is TraceabilityResponses.ComponentTraceabilityRequestItem =>
  item.type === "component"

export const isMaterialItem = (
  item: TraceabilityResponses.TraceabilityRequestItem
): item is TraceabilityResponses.MaterialTraceabilityRequestItem =>
  item.type === "material"

export const allChildrenItems = (
  operationItems: OperationItem[],
  nodeId: OperationNodeId
): OperationItem[] => {
  const children = operationItems.filter(
    (item) => item.parentNodeId === nodeId && item.isInInformationRequest
  )
  return children.flatMap((child) => [
    child,
    ...allChildrenItems(operationItems, child.nodeId),
  ])
}

export const loadSavedState = (
  requestId: number,
  userId: number
): Record<OperationNodeId, OperationState> => {
  const savedState = localStorage.getItem(`requestState-${requestId}-${userId}`)
  if (!savedState) return {}
  return JSON.parse(savedState) as Record<OperationNodeId, OperationState> // TODO: should use zod to parse this
}

export const writeSavedState = (
  requestId: number,
  userId: number,
  state: Record<OperationNodeId, OperationState>
) => {
  localStorage.setItem(
    `requestState-${requestId}-${userId}`,
    JSON.stringify(state)
  )
}

export function canSubmitRequest(
  operationItems: OperationItem[],
  user: LibraryResponses.TFGUser | null
): boolean {
  if (!user) return false

  const totalOperationItems = operationItems.length
  const completedOperationItems = operationItems.filter(
    (oi) => oi.operationCompletionStatus?.isComplete
  ).length

  return totalOperationItems === completedOperationItems
}

export function getComponentComposition(
  item: TraceabilityResponses.ComponentTraceabilityRequestItem,
  getTranslation: GetTranslationCallback
) {
  const composition = item.children
    .map(
      (m) =>
        `${getTranslation(m.itemInformation.type)} ${
          m.itemInformation.proportion
        }%`
    )
    .join(", ")

  return composition
}

export function requestsToRequestsWithOp({
  requests,
  operationChains,
  user,
}: {
  requests?: TraceabilityResponses.TraceabilityRequest[]
  operationChains: FindOperationChainsResponseDto[]
  user: LibraryResponses.TFGUser | null
}) {
  if (!user) return []
  return requests
    ? requests.map((request) => {
        const customFieldDefByActivity = _.groupBy(
          request.customFieldDefinitions.filter(
            (cfd) => cfd.forActivityId && cfd.onActivityId === null
          ),
          (cfd) => cfd.forActivityId
        )

        const extraCustomFieldDefByOnActivity = _.groupBy(
          request.customFieldDefinitions.filter(
            (cfd) =>
              cfd.onActivityId &&
              cfd.forActivityId !== null &&
              cfd.forActivityId !== undefined
          ),
          (cfd) => cfd.onActivityId
        )

        const hiddenCustomFieldDefs = _.groupBy(
          request.customFieldDefinitions.filter(
            (cfd) =>
              cfd.forActivityId &&
              cfd.onActivityId !== null &&
              cfd.onActivityId === request.activityId
          ),
          (cfd) => cfd.forActivityId
        )

        const { opItems, error } = buildOperationTree({
          startingItem: request.item,
          customFieldDefByActivity,
          extraCustomFieldDefs: extraCustomFieldDefByOnActivity,
          hiddenCustomFieldDefs,
          startingActivityName: request.activityName,
          operationChains,
          isReinforcedTraceability: request.reinforcedTraceability,
          informationRequestId: request.id,
          informationRequestOwner: user.id,
        })
        const customFieldValues = opItems
          .flatMap((item) => {
            return (
              item.operation?.customFieldsValues?.map((field) => {
                return {
                  ...field,
                }
              }) ?? []
            )
          })
          .filter(
            (field): field is TraceabilityResponses.CustomFieldValue =>
              field !== undefined
          )
        for (const opItem of opItems) {
          if (opItem.operation) {
            opItem.operationCompletionStatus = getOperationCompletionStatus(
              opItem,
              opItems,
              customFieldValues
            )
          }
        }
        const opItemsByRank = groupBy(opItems, (oi) => oi.rank)
        return { original: request, opItems, opItemsByRank, error }
      })
    : []
}

export const nodeRequiresRef = (
  parentOpItem: TraceabilityOperation | null,
  opItem: TraceabilityOperation | null
): boolean => {
  if (!opItem) return false
  const doNotKnow = opItem.isTransferred ?? false

  const parentDoNotKnow = parentOpItem?.isTransferred ?? false

  return doNotKnow && !parentDoNotKnow
}

export const isOperationOwner = (
  opItem: TraceabilityOperation,
  user: LibraryResponses.TFGUser | null
) => {
  if (!user) return false
  return !opItem.owner || opItem.owner.id === user.id
}

export const getOpenAndClosedProdsiteFieldRequests = (
  productionSiteFieldRequests:
    | SupplierResponses.ProdsitefieldRequest[]
    | undefined,
  userId: string | undefined
) => {
  const openRequests =
    productionSiteFieldRequests?.filter(
      (pfr) => !isProdsiteFieldRequestSubmitted(pfr, userId)
    ) || []

  const closedRequests =
    productionSiteFieldRequests?.filter((pfr) =>
      isProdsiteFieldRequestSubmitted(pfr, userId)
    ) || []

  return { openRequests, closedRequests }
}

export const getIsValidRef = (
  parentOpItem: TraceabilityOperation | undefined,
  operationItem: TraceabilityOperation | undefined
): boolean => {
  if (!operationItem) return true
  const mandatoryRef = nodeRequiresRef(
    parentOpItem ?? null,
    operationItem ?? null
  )

  return !!(
    !mandatoryRef ||
    (operationItem?.reference !== "" && operationItem?.purchaseOrderRef !== "")
  )
}

export const getIsTransferred = (
  operation: TraceabilityResponses.TraceabilityRequestOperation | undefined,
  isReinforcedTraceability: boolean,
  isInInformationRequest: boolean
): boolean => {
  return (
    operation?.owner.id !== operation?.contact.userId ||
    isReinforcedTraceability ||
    !isInInformationRequest
  )
}

export const getIsReinforcedTraceability = (
  request: RequestWithOpItem,
  operationItem: OperationItem
): boolean => {
  return request.original.reinforcedTraceability && operationItem.rank >= 2
}

export type OperationCompletionStatus = {
  isComplete: boolean
  progress?: number
}

export const getOperationCompletionStatus = (
  operation: OperationItem,
  opItems: OperationItem[],
  customFieldValues: TraceabilityResponses.CustomFieldValue[]
): OperationCompletionStatus => {
  if (!operation.operation) {
    return { isComplete: true, progress: 100 }
  }
  const progress = calculateOperationProgress(
    operation.operation.isTransferred ?? false,
    operation,
    opItems,
    customFieldValues
  )

  return { isComplete: progress === 100, progress }
}

export const getIsComplete = (
  operation: OperationItem,
  user: LibraryResponses.TFGUser | null,
  isReinforcedTraceability: boolean
): boolean => {
  if (!operation.operation) return true

  if (!operation.isInInformationRequest) return true

  const isTransferred = getIsTransferred(
    operation.operation,
    isReinforcedTraceability && operation.rank >= 2,
    operation.isInInformationRequest
  )

  const isRefValid = getIsValidRef(
    operation.parentOperation,
    operation.operation
  )
  if (!isOperationOwner(operation.operation, user)) return true

  if (isTransferred) {
    return !!(
      operation.operation?.contact.id &&
      isRefValid &&
      operation.operation?.purchaseOrderRef
    )
  }
  return !!(
    operation.operation?.productionSiteId &&
    isRefValid &&
    isCustomFieldComplete(operation)
  )
}

const getProgressStandardFields = (
  isTransferred: boolean,
  isMandatoryRef: boolean
): string[] => [
  ...(isTransferred ? [] : ["productionSiteId"]),
  ...(isMandatoryRef ? ["reference", "purchaseOrderRef"] : []),
]

export const isCustomFieldValueComplete = (
  fieldValue: unknown,
  documentRequirement: keyof typeof CustomFieldDocumentRequirement,
  document?: {
    id: string
    originalName: string | null
  } | null
): boolean => {
  if (fieldValue === undefined || fieldValue === null || fieldValue === "")
    return false
  if (typeof fieldValue === "string" && fieldValue.trim() === "") return false
  if (Array.isArray(fieldValue) && fieldValue.length === 0) return false
  if (
    documentRequirement === CustomFieldDocumentRequirement.REQUIRED &&
    !document
  )
    return false
  return true
}

const getCompletedCustomFieldsCount = (
  isTransferred: boolean,
  operation: OperationItem,
  extraCustomFieldDefOperationItems: OperationItem[],
  customFieldValues: TraceabilityResponses.CustomFieldValue[]
): number => {
  if (isTransferred) return 0

  const completedCustomFields = operation.customFieldDefs.filter((cfd) => {
    const fieldValue = customFieldValues.find(
      (cfv) =>
        cfv.customFieldDefinitionId === cfd.id &&
        cfv.operationId === operation.operation?.id
    )
    if (!fieldValue) return false
    return isCustomFieldValueComplete(
      fieldValue.value,
      cfd.documentRequirement,
      fieldValue.document
    )
  }).length

  const completedExtraCustomFields = extraCustomFieldDefOperationItems
    .flatMap((oi) => {
      return oi.customFieldDefs.filter((cfd) => {
        const fieldValue = customFieldValues.find(
          (cfv) =>
            cfv.customFieldDefinitionId === cfd.id &&
            cfv.operationId === oi.operation?.id
        )
        if (!fieldValue) return false
        return isCustomFieldValueComplete(
          fieldValue.value,
          cfd.documentRequirement,
          fieldValue.document
        )
      }).length
    })
    .filter((num) => num !== undefined)
    .reduce((acc, curr) => acc + curr, 0)

  return completedCustomFields + completedExtraCustomFields
}

export const calculateOperationProgress = (
  isTransferred: boolean,
  operation: OperationItem,
  opItems: OperationItem[],
  customFieldValues: TraceabilityResponses.CustomFieldValue[]
): number => {
  const OPERATION_PROGRESS_COMPLETE = 100 as const
  if (!operation.isInInformationRequest) return OPERATION_PROGRESS_COMPLETE

  const extraCustomFieldDefOperationItems = opItems.flatMap((oi) => {
    const extraCustomFieldDefs = operation.extraCustomFieldDefs?.filter(
      (cfd) => cfd.forActivityId === oi.activityId
    )

    if (extraCustomFieldDefs.length > 0) {
      return {
        ...oi,
        customFieldDefs: extraCustomFieldDefs,
      }
    }
    return []
  })
  const totalExtraCustomFields = extraCustomFieldDefOperationItems.reduce(
    (acc, oi) => acc + oi.customFieldDefs.length,
    0
  )

  const totalCustomFields = isTransferred
    ? 0
    : operation.customFieldDefs.length + totalExtraCustomFields

  const isMandatoryRef = nodeRequiresRef(
    operation.parentOperation ?? null,
    operation.operation ?? null
  )
  const totalRequiredFields =
    totalCustomFields +
    getProgressStandardFields(isTransferred, isMandatoryRef).length
  const completedCustomFields = getCompletedCustomFieldsCount(
    isTransferred,
    operation,
    extraCustomFieldDefOperationItems,
    customFieldValues
  )

  const hasProductionSite = Boolean(
    !isTransferred && operation.operation?.productionSiteId
  )
  const hasReference = Boolean(operation.operation?.reference && isMandatoryRef)
  const hasPurchaseOrder = Boolean(
    operation.operation?.purchaseOrderRef && isMandatoryRef
  )

  const standardFieldsChecks = [
    hasProductionSite,
    hasReference,
    hasPurchaseOrder,
  ]

  const completedStandardFieldsCount =
    standardFieldsChecks.filter(Boolean).length

  const totalCompletedFields =
    completedStandardFieldsCount + completedCustomFields

  return totalRequiredFields === 0
    ? OPERATION_PROGRESS_COMPLETE
    : (totalCompletedFields / totalRequiredFields) * 100
}

export const parseRequestOrderDate = (
  request: RequestWithOpItem
): string | undefined => {
  if (request.original.item.type === "product") {
    return request.original.item.itemInformation.date
  }
}

export const parseRequestDueDate = (
  request: RequestWithOpItem
): string | undefined => {
  if (request.original.item.type === "product") {
    return request.original.item.marketLaunchDate?.toLocaleString()
  }
}

export function hasOperation(item: OperationItem): item is Omit<
  OperationItem,
  "operation"
> & {
  operation: TraceabilityOperation
} {
  return item.operation !== null
}

export function assignDuplicateProductionSites(
  operationItems: OperationItem[]
): OperationItem[] {
  const productionSiteCounts: Record<string, number> = {}

  // Count occurrences of each production site ID
  for (const { operation } of operationItems) {
    const productionSiteId = operation?.productionSiteId
    if (!productionSiteId) continue
    productionSiteCounts[productionSiteId] =
      (productionSiteCounts[productionSiteId] || 0) + 1
  }

  // Mark duplicates
  return operationItems.map((opItem) => {
    const productionSiteId = opItem.operation?.productionSiteId
    if (productionSiteId && productionSiteCounts[productionSiteId] > 1) {
      return {
        ...opItem,
        isDuplicateProductionSite: true,
      }
    }
    return opItem
  })
}
